import React, { useState, useEffect, useMemo } from "react";
import "@styles/pages/GenericPage/VideoBlogSummaryPage/SummaryCardList.scss";
import {
  Box,
  Typography,
  Button,
  Dialog,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { useTranslation, Trans } from "react-i18next";
import DropdownFilterMenu from "@components/ui/DropdownFilterMenu";

import { useNavigate } from "react-router-dom";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SortIcon from "@mui/icons-material/Sort";

import { addClickRate, getRelateBlogList } from "@api/landingApi";
import VideoPlayer from "@components/ui/VideoPlayer";
import GenericSwiper from "@components/ui/GenericSwiper";
import { genericLatestPopularFilterData } from "@data/FilterData";
import { gaEvent } from "@utils/gaEvent";

const SummaryCardList = ({
  cardType, // "video" | "blog"
  filterData, // 定義陣列（含 type / displayField / valueField / staticOptions 等）
  filterOptions = {}, // 各 type 的 options 映射（由父層拉 API 傳入）
  cardList, // 內容清單（video 或 blog）
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const isChinese = lang === "hk";
  const isMobile = useMediaQuery("(max-width:425px)");

  const [selectedValues, setSelectedValues] = useState({});
  const [visibleCount, setVisibleCount] = useState(9);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [relateBlogList, setRelateBlogList] = useState([]);

  // Mobile dialog
  const [mobileOpen, setMobileOpen] = useState(false);

  // 追蹤點擊
  const postClickRate = async ({ id, article_type }) => {
    try {
      const res = await addClickRate({ id, article_type });
      console.log("Click tracked:", res.data.StatusCode);
    } catch (err) {
      console.error(err);
    }
  };

  // 拉相關 Blog
  const fetchRelateBlogList = async ({ hashtag }) => {
    setRelateBlogList([]);
    try {
      const res = await getRelateBlogList({ hashtag });
      setRelateBlogList(res.data.Data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Filter 改變
  const handleFilterChange = (type, selectedItem) => {
    const field =
      filterData.find((f) => f.type === type)?.valueField || "value";
    setSelectedValues((prev) => ({
      ...prev,
      [type]: selectedItem?.[field],
    }));
  };

  // Reset 全部
  const handleResetAllFilters = () => {
    const init = {};
    filterData.forEach((f) => (init[f.type] = ""));
    init.latest_most_popular = 0;
    setSelectedValues(init);
  };

  // 每次條件改變，重置可見數量
  useEffect(() => {
    setVisibleCount(9);
  }, [selectedValues]);

  // 過濾/排序
  const fieldMap = {
    video_product: "product",
    video_theme: "theme",
    blog_product: "product",
    blog_theme: "theme",
  };

  const filteredCards = useMemo(() => {
    return cardList.filter((item) => {
      return Object.entries(selectedValues).every(([key, value]) => {
        if (!value) return true;
        if (key === "latest_most_popular") return true;

        const targetField = fieldMap[key];
        if (!targetField) return true;
        if (targetField === "product") {
          const itemThemes = (item.product?.split(",") || []).map((s) =>
            s.trim()
          );
          return itemThemes.includes(String(value));
        }
        if (targetField === "theme") {
          const itemThemes = (item.theme?.split(",") || []).map((s) =>
            s.trim()
          );
          return itemThemes.includes(String(value));
        }
        return String(item[targetField]) === String(value);
      });
    });
  }, [cardList, selectedValues]);

  const sortedCards = useMemo(() => {
    const sortValue = Number(selectedValues.latest_most_popular);
    const cloned = [...filteredCards];
    if (sortValue === 1) {
      // 最熱門
      return cloned.sort((a, b) => (b.view_no || 0) - (a.view_no || 0));
    }
    // 最新
    return cloned.sort((a, b) => Number(b.id) - Number(a.id));
  }, [filteredCards, selectedValues.latest_most_popular]);

  const visibleCards = sortedCards.slice(0, visibleCount);

  const activeVideo = cardList.find((v) => v.id === activeVideoId);

  return (
    <Box className="summary-card-list-container">
      {/* ====== Filters ====== */}
      {!isMobile ? (
        // Desktop / Tablet：原本的 Filter Bar
        <Box className="filters-container">
          {filterData.map((filter) => (
            <DropdownFilterMenu
              key={filter.type}
              options={filterOptions[filter.type] || []}
              displayField={filter.displayField[lang]}
              valueField={filter.valueField}
              lang={lang}
              value={selectedValues[filter.type]}
              onChange={(item) => handleFilterChange(filter.type, item)}
            />
          ))}
          {/* 最新/最熱門 */}
          <DropdownFilterMenu
            options={genericLatestPopularFilterData}
            displayField={(item) =>
              isChinese ? item.desc_hk : item.desc_hk_en
            }
            valueField="value"
            lang={lang}
            value={selectedValues.latest_most_popular}
            onChange={(item) => handleFilterChange("latest_most_popular", item)}
            icon="sort"
          />
          <Button
            className="dropdown-button reset-filter-button"
            onClick={handleResetAllFilters}
          >
            <Trans i18nKey="generic:reset" />
            <RestartAltIcon className="reset-icon" />
          </Button>
        </Box>
      ) : (
        // Mobile：Filter Button + Dialog
        <>
          <Box className="dialog-mobile-filter-button-container">
            <Button
              className="dropdown-button mobile-filter-button"
              onClick={() => setMobileOpen(true)}
            >
              <FilterAltIcon />
              <Trans i18nKey="generic:filter" />
            </Button>
          </Box>

          <Dialog
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            fullScreen
            PaperProps={{ className: "dialog-filter-container" }}
            sx={{
              "& .MuiDialog-paper": {
                position: "fixed",
                bottom: 0,
                maxHeight: "50vh",
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                border: "2px solid #f5f5f5",
                background: "#fbfbfb",
              },
            }}
          >
            <Box className="dialog-top-container">
              <Typography className="dialog-title">
                {cardType === "video" ? (
                  <Trans i18nKey="generic:latest_video" />
                ) : (
                  <Trans i18nKey="generic:blog" />
                )}
              </Typography>
              <IconButton
                onClick={() => setMobileOpen(false)}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box className="dialog-content">
              {/* 排序 */}
              <Typography className="dialog-text">
                <SortIcon className="dialog-icon" />
                <Trans i18nKey="generic:sort" />
              </Typography>
              <Box className="toggle-button-container">
                <ToggleButtonGroup
                  value={selectedValues.latest_most_popular ?? 0}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      handleFilterChange("latest_most_popular", {
                        value: newValue,
                      });
                    }
                  }}
                >
                  <ToggleButton value={0}>
                    <Trans i18nKey="generic:most_latest" />
                  </ToggleButton>
                  <ToggleButton value={1}>
                    <Trans i18nKey="generic:most_popular" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box className="dialog-line" />

              {/* 篩選 Selects */}
              <Typography className="dialog-text">
                <FilterAltIcon className="dialog-icon" />
                <Trans i18nKey="generic:filter" />
              </Typography>

              {filterData.map((filter) => {
                const langKey = filter.displayField?.[lang];
                const valueField = filter.valueField || "value";
                const options = filterOptions[filter.type] || [];
                const currentValue =
                  selectedValues?.[filter.type] ??
                  (options.length > 0 ? options[0][valueField] : "");

                return (
                  <Box className="select-container" key={filter.type}>
                    <Select
                      className="select-filter"
                      variant="outlined"
                      value={currentValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        const selectedItem =
                          options.find(
                            (o) => String(o?.[valueField]) === String(v)
                          ) || null;
                        handleFilterChange(filter.type, selectedItem);
                      }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: "20vh" } } }}
                    >
                      {options.map((opt) => (
                        <MenuItem key={opt[valueField]} value={opt[valueField]}>
                          {langKey
                            ? opt[langKey]
                            : opt.label || opt.name || String(opt[valueField])}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                );
              })}

              <Box className="dialog-line" />

              <Box className="dialog-button-container">
                <Button
                  className="button dialog-reset-filter-button"
                  onClick={handleResetAllFilters}
                >
                  <Trans i18nKey="generic:reset" />
                  <RestartAltIcon className="reset-icon" />
                </Button>
                <Button
                  className="loading-button"
                  onClick={() => setMobileOpen(false)}
                >
                  <Trans i18nKey="generic:view_results" />
                </Button>
              </Box>
            </Box>
          </Dialog>
        </>
      )}
      {/* ====== Cards ====== */}
      {sortedCards.length === 0 ? (
        <Typography className="coming-soon-text" variant="h6">
          <Trans i18nKey="home:coming_soon" />
        </Typography>
      ) : (
        <Box className="card-section">
          {visibleCards.map((item, index) => (
            <Box
              className="card-container"
              key={index}
              onClick={() => {
                if (cardType === "video") {
                  gaEvent("clubs_video","video","video_click",item.id);
                  postClickRate({ id: item.id, article_type: cardType });
                  if (item?.hashtag)
                    fetchRelateBlogList({ hashtag: item?.hashtag });
                  setActiveVideoId(item.id);
                } else {
                  gaEvent("clubs_blog","blog","blog_click",item.id);
                  navigate(`/blog_article?blog_id=${item.id}`);
                }
              }}
            >
              <Box className="card-image-container">
                {cardType === "video" && (
                  <Box className="video-player-icon-container">
                    <PlayArrowRoundedIcon className="video-player-icon" />
                  </Box>
                )}
                <img
                  className="card-image"
                  alt={index}
                  src={isChinese ? item.thumbnail_hk : item.thumbnail_hk_en}
                />
              </Box>
              <Box className="detail-container">
                <Box className="title-button-container">
                  <Typography className="detail-title">
                    {isChinese ? item.title_hk : item.title_hk_en}
                  </Typography>
                </Box>
                <Typography className="description">
                  {isChinese ? item.desc_hk : item.desc_hk_en}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {/* See More */}
      {visibleCount < sortedCards.length && (
        <Box className="button-container">
          <Button
            className="loading-button"
            onClick={() => setVisibleCount((prev) => prev + 9)}
          >
            <Trans i18nKey="generic:explore_more" />
          </Button>
        </Box>
      )}
      {/* ====== Video Player Popup（含相關 Blog） ====== */}
      {cardType === "video" && activeVideoId && activeVideo && (
        <Box
          className="video-popup-overlay"
          onClick={() => setActiveVideoId(null)}
        >
          <Box className="video-popup-outline">
            <Box
              className="video-popup-content"
              onClick={(e) => e.stopPropagation()}
            >
              <CloseIcon
                className="popup-close-button"
                onClick={() => setActiveVideoId(null)}
              />
              <Box className="popup-video">
                <VideoPlayer videoUrl={activeVideo.url} />
              </Box>
              <Typography className="popup-title">
                {isChinese ? activeVideo.title_hk : activeVideo.title_hk_en}
              </Typography>
              <Box className="popup-description-container">
                <Typography className="popup-description">
                  {isChinese ? activeVideo.desc_hk : activeVideo.desc_hk_en}
                </Typography>
              </Box>

              {relateBlogList.length > 0 && (
                <Box className="related-section">
                  <Typography className="related-title">
                    <Trans i18nKey="generic:related_blog" />
                  </Typography>
                  <GenericSwiper
                    type="blog"
                    slides={relateBlogList.map((s) => ({
                      ...s,
                      image: s.thumbnail_hk
                        ? isChinese
                          ? s.thumbnail_hk
                          : s.thumbnail_hk_en
                        : s.thumbnail || s.image,
                      title: isChinese
                        ? s.title_hk || s.title
                        : s.title_hk_en || s.title,
                      description: isChinese
                        ? s.desc_hk || s.description
                        : s.desc_hk_en || s.description,
                      url: s?.url,
                    }))}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SummaryCardList;
